import * as React from 'react';

import styled from 'styled-components';

import { COLOR, FONT_SIZE, FONT, SPACING } from '../../packages/constants';
import Star from '../../packages/icons/star';
import { StyledIcon as GalleryIcon } from '../gallery-icon/gallery-icon';

export interface GalleryItemInfoProps {
    onClick?: (e: any) => void;
    iconName: string;
    title: string;
    description: string;
    imagePath?: string;
    reportCodes?: string[];
    hideFavourites?: boolean;
    isFavourite?: boolean;
    assistAccess: boolean;
    themeProvider?: {};
}

export interface StyledButtonProps {
    assistAccess: boolean;
}

const StyledGalleryItemInfo = styled.div`
    background-color: ${COLOR.PRIMARY.WHITE};
    box-shadow: inset 7px 0 9px -7px rgba(0, 0, 0, 0.15);
    width: 100%;
    height: 100%;
    overflow: auto;
`;

const StyledGalleryItemInfoInner = styled.div`
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    padding: 23px ${SPACING.X_LARGE} ${SPACING.X_LARGE};
    max-width: 240px;
    width: 100%;
    height: 100%;
    overflow: auto;
`;

const StyledGalleryIcon = styled(GalleryIcon)`
    margin-left: 44px;
`;

const StyledGalleryItemTitle = styled.h2`
    font-family: ${FONT.OPEN_SANS};
    font-size: ${FONT_SIZE.LARGE};
    font-weight: 500;
    line-height: ${FONT_SIZE.X_LARGE};
    text-align: center;
    margin-bottom: 18px;
    color: ${COLOR.MAIN.LABEL_TEXT};
    width: 100%;
`;

const StyledDescription = styled.p`
    font-family: ${FONT.OPEN_SANS};
    font-size: ${FONT_SIZE.SMALL};
    font-weight: 400;
    line-height: ${FONT_SIZE.LARGE};
    margin-bottom: ${SPACING.X_LARGE};
    align-self: flex-start;
    color: ${COLOR.MAIN.LABEL_TEXT};
    width: 100%;
`;

const StyledHeading = styled.h3`
    font-family: ${FONT.OPEN_SANS};
    font-size: ${FONT_SIZE.SMALL};
    font-weight: 700;
    align-self: flex-start;
    color: ${COLOR.MAIN.LABEL_TEXT};
    margin-top: ${SPACING.X_SMALL};
    margin-bottom: ${SPACING.X_SMALL};
    width: 100%;
`;

const StyledList = styled.ul`
    display: flex;
    flex-wrap: wrap;
    font-family: ${FONT.OPEN_SANS};
    font-size: ${FONT_SIZE.SMALL};
    font-weight: 400;
    line-height: ${FONT_SIZE.MEDIUM};
    margin: 0;
    padding: 0;
    list-style-type: none;
    align-self: flex-start;
    color: ${COLOR.MAIN.LABEL_TEXT};
    width: 100%;
`;

const StyledStar = styled(Star)`
    width: 20px;
    margin-right: ${SPACING.MEDIUM};
`;

const StyledFavouriteButton = styled.button<StyledButtonProps>`
    font-family: ${FONT.OPEN_SANS};
    font-size: ${FONT_SIZE.SMALL};
    font-weight: 400;
    line-height: ${FONT_SIZE.MEDIUM};
    display: flex;
    justify-content: space-between;
    align-items: center;
    align-self: flex-start;
    background-color: ${COLOR.PRIMARY.WHITE};
    color: ${COLOR.MAIN.DARK_GREY};
    border: 0;
    padding: ${SPACING.SMALL} ${SPACING.SMALL} ${SPACING.SMALL} 0;
    margin-top: auto;
    cursor: pointer;

    &:focus {
        outline: ${(props: StyledButtonProps): string => (props.assistAccess ? '1px dotted #212121' : '0')};
        outline: ${(props: StyledButtonProps): string =>
            props.assistAccess ? '5px auto -webkit-focus-ring-color' : '0'};
    }

    &:hover ${StyledStar} {
        fill: ${COLOR.PRIMARY.YELLOW};
    }
`;

const StyledListItem = styled.li`
    font-family: ${FONT.OPEN_SANS};
    font-size: ${FONT_SIZE.X_SMALL};
    font-weight: 400;
    line-height: ${FONT_SIZE.LARGE};
    align-self: flex-start;
    color: ${COLOR.MAIN.LABEL_TEXT};
    margin-right: ${SPACING.X_SMALL};

    &::after {
        content: ',';
    }

    &:last-child {
        margin-right: 0;

        &::after {
            content: '';
        }
    }
`;

export const GalleryItemInfo = React.forwardRef<HTMLDivElement, GalleryItemInfoProps>(
    (
        {
            iconName,
            title,
            description,
            reportCodes,
            isFavourite = false,
            hideFavourites,
            assistAccess,
            imagePath,
            ...props
        },
        ref,
    ) => {
        const [favourite, setFavourite] = React.useState(isFavourite);
        const handleFavourite = () => {
            setFavourite(!favourite);
        };

        return (
            <StyledGalleryItemInfo ref={ref} {...props}>
                <StyledGalleryItemInfoInner>
                    <StyledGalleryIcon iconName={iconName} imagePath={imagePath} />
                    <StyledGalleryItemTitle>{title}</StyledGalleryItemTitle>
                    <StyledDescription>{description}</StyledDescription>
                    {reportCodes && reportCodes.length > 0 && (
                        <>
                            <StyledHeading>Report codes</StyledHeading>
                            <StyledList>
                                {reportCodes.map((code: string, index: number) => (
                                    <StyledListItem key={`reportCode-${index}`}>{code}</StyledListItem>
                                ))}
                            </StyledList>
                        </>
                    )}
                    {!hideFavourites && (
                        <StyledFavouriteButton assistAccess={assistAccess} onClick={handleFavourite}>
                            <StyledStar fill={favourite ? COLOR.PRIMARY.YELLOW : COLOR.MAIN.MEDIUM_GREY} />
                            {favourite ? 'Remove from' : 'Add to'} favourites
                        </StyledFavouriteButton>
                    )}
                </StyledGalleryItemInfoInner>
            </StyledGalleryItemInfo>
        );
    },
);
GalleryItemInfo.displayName = 'GalleryItemInfo';
